import React, { useState, useEffect } from 'react';
import ApiRequestService from '../component/ApiRequestService';

const AssessmentObjectivesAnswerGrid = ({ selectedSubcategoryKeys = [] }) => {
    const [groups, setGroups] = useState([]);
    const [checkStates, setCheckStates] = useState({});
    const [expanded, setExpanded] = useState({}); // category_name → bool

    // Sorted so order changes don't trigger a refetch
    const keysStr = [...selectedSubcategoryKeys].sort().join(',');

    useEffect(() => {
        if (!keysStr) { setGroups([]); return; }

        // Parse "subjectId_topicId_categoryId_subcategoryId"
        const parsed = keysStr.split(',').map(key => {
            const [subject_id, topic_id, category_id, subcategory_id] = key.split('_').map(Number);
            return { key, subject_id, topic_id, category_id, subcategory_id };
        });

        // Group by subject + topic + category
        const byCategory = {};
        parsed.forEach(p => {
            const ck = `${p.subject_id}_${p.topic_id}_${p.category_id}`;
            if (!byCategory[ck]) byCategory[ck] = { ...p, items: [] };
            byCategory[ck].items.push(p);
        });

        // Fetch category + subcategory names for each group
        Promise.all(
            Object.values(byCategory).map(async (group) => {
                const [catRes, subRes] = await Promise.all([
                    ApiRequestService({
                        method: 'post', url: '/getAllCategories',
                        data: { subject_id: group.subject_id, topic_id: group.topic_id },
                    }),
                    ApiRequestService({
                        method: 'post', url: '/getAllSubCategories',
                        data: { subject_id: group.subject_id, topic_id: group.topic_id, category_id: group.category_id },
                    }),
                ]);

                const categories = catRes?.data?.data?.category_names || [];
                const subcategories = subRes?.data?.data?.subCategory_names || [];
                const selectedIds = new Set(group.items.map(i => String(i.subcategory_id)));

                const cat = categories.find(c => String(c.category_id) === String(group.category_id));

                return {
                    category_name: cat?.category_name || '',
                    subcategories: subcategories
                        .filter(sc => selectedIds.has(String(sc.subcategory_id)))
                        .map(sc => ({
                            key: group.items.find(i => String(i.subcategory_id) === String(sc.subcategory_id)).key,
                            subcategory_name: sc.subcategory_name,
                        })),
                };
            })
        ).then(setGroups);
    }, [keysStr]);

    const getState = (key) => checkStates[key] || { covered: false, wantToAccess: false };

    const toggleCovered = (key) => {
        setCheckStates(prev => {
            const cur = prev[key] || { covered: false, wantToAccess: false };
            const nextCovered = !cur.covered;
            return {
                ...prev,
                [key]: {
                    covered: nextCovered,
                    wantToAccess: nextCovered ? cur.wantToAccess : false,
                },
            };
        });
    };

    const toggleWantToAccess = (key) => {
        setCheckStates(prev => {
            const cur = prev[key] || { covered: false, wantToAccess: false };
            const nextWant = !cur.wantToAccess;
            return {
                ...prev,
                [key]: {
                    covered: nextWant ? true : cur.covered,
                    wantToAccess: nextWant,
                },
            };
        });
    };

    const toggleAllCovered = (group) => {
        setCheckStates(prev => {
            const next = { ...prev };
            const allCovered = group.subcategories.every(sc => (next[sc.key] || { covered: false }).covered);
            group.subcategories.forEach(sc => {
                const cur = next[sc.key] || { covered: false, wantToAccess: false };
                next[sc.key] = allCovered
                    ? { covered: false, wantToAccess: false }
                    : { ...cur, covered: true };
            });
            return next;
        });
    };

    const toggleAllWantToAccess = (group) => {
        setCheckStates(prev => {
            const next = { ...prev };
            const allWant = group.subcategories.every(sc => (next[sc.key] || { wantToAccess: false }).wantToAccess);
            group.subcategories.forEach(sc => {
                const cur = next[sc.key] || { covered: false, wantToAccess: false };
                next[sc.key] = allWant
                    ? { ...cur, wantToAccess: false }
                    : { covered: true, wantToAccess: true };
            });
            return next;
        });
    };

    if (groups.length === 0) return null;

    return (
        <div style={{ marginTop: 16 }}>
            {groups.map(group => {
                const isOpen = !!expanded[group.category_name];
                return (
                    <div key={group.category_name} style={{ marginBottom: 8 }}>
                        <div
                            style={{ fontWeight: 600, fontSize: 14, padding: '8px 0', cursor: 'pointer', userSelect: 'none',
                                background: "#f0f0f0",
                                borderRadius:'4px',
                                color:"#747474"
                             }}
                            onClick={() => setExpanded(prev => ({ ...prev, [group.category_name]: !prev[group.category_name] }))}
                        >
                            <span style={{ display: 'inline-block', width: 16, marginRight: 4 }}>{isOpen ? '▾' : '▸'}</span>
                            {group.category_name}
                        </div>
                        {isOpen && (
                            <table className="w-100" style={{ marginBottom: 0, borderCollapse: 'separate', borderSpacing: '0px 10px'  }}>
                                <thead style={{ background: '#e8e8e8', fontSize:'14px', padding:"8px 4px", color:"#747474" }}>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Assessment Objective</th>
                                        <th style={{ textAlign: 'center', width: 100 }}>
                                            Covered
                                            <div>
                                                <input
                                                    style={{
                                                        width: "18px",
                                                        height: "18px",
                                                    }}
                                                    type="checkbox"
                                                    checked={group.subcategories.every(sc => getState(sc.key).covered)}
                                                    onChange={() => toggleAllCovered(group)}
                                                />
                                            </div>
                                        </th>
                                        <th style={{ textAlign: 'center', width: 100 }}>
                                            Want to Assess
                                            <div>
                                                <input
                                                    style={{
                                                        width: "18px",
                                                        height: "18px",
                                                    }}
                                                    type="checkbox"
                                                    checked={group.subcategories.every(sc => getState(sc.key).wantToAccess)}
                                                    onChange={() => toggleAllWantToAccess(group)}
                                                />
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.subcategories.map(sc => {
                                        const st = getState(sc.key);
                                        return (
                                            <tr key={sc.key}>
                                                <td>{sc.subcategory_name}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        style={{
                                                            width: "18px",
                                                            height: "18px",
                                                        }}
                                                        type="checkbox"
                                                        checked={st.covered}
                                                        onChange={() => toggleCovered(sc.key)}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        style={{
                                                            width: "18px",
                                                            height: "18px",
                                                        }}
                                                        type="checkbox"
                                                        checked={st.wantToAccess}
                                                        onChange={() => toggleWantToAccess(sc.key)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AssessmentObjectivesAnswerGrid;
